<script setup>
import { storeToRefs } from "pinia";
import { computed, ref, onMounted, onBeforeMount, onBeforeUnmount } from "vue";
import AgentLayout from "./layouts/AgentLayout.vue";
import PageHeader from "@/components/shared/PageHeader.vue";
import moment from "moment";
import { useGridPreferencesStore } from "@/stores/gridPreferences";
import { useIntegrationsStore } from "@/stores/integrations";
import {
  DxDataGrid,
  DxColumn,
  // DxFilterRow,
  DxHeaderFilter,
  DxToolbar,
  DxItem,
  DxPaging,
  DxPager,
  DxSorting,
  DxScrolling,
  DxColumnChooser,
  DxLoadPanel,
  DxStateStoring,
  DxSearchPanel,
} from "devextreme-vue/data-grid";
// import DxDropDownButton from "devextreme-vue/drop-down-button";
// import { default as Button } from "devextreme-vue/button";
import { exportDataGrid } from "devextreme/excel_exporter";
import { exportDataGrid as exportDataGridToPdf } from "devextreme/pdf_exporter";
import { Workbook } from "exceljs";
import saveAs from "file-saver";
import { jsPDF } from "jspdf";
import { useCompanyStore } from "@/stores/company";

const { companyId } = storeToRefs(useCompanyStore());
const adminPreferenceId = computed(() =>
  localStorage.getItem("adminPreferenceId"),
);
// let getModalState = computed(()=> store.getters.getModalState);
// import useValidation from '@/utils/helpers/validations';

// const { checkLoggedInUserRole } = useValidation();
const gridPreferencesStore = useGridPreferencesStore();
const integrationsStore = useIntegrationsStore();
const dataGridRefName = ref(null);
let showClearAll = ref(false);
const allowedOperations = ref(["contains"]);
const JOB_POLL_INTERVAL_MS = 15000;
let jobsGrid = ref([]);
let jobsInterval = null;

const dataGrid = computed(() => {
  return dataGridRefName.value.instance;
});

const contentReady = (e) => {
  let sortedExp = getSortedColumns();
  let filterExp = e.component.getCombinedFilter();
  if (filterExp || sortedExp) {
    showClearAll.value = true;
  } else {
    showClearAll.value = false;
  }
  setColumnChooserPosition();
};

const getSortedColumns = () => {
  let allColumns = Array.from(Array(dataGrid.value.columnCount()).keys()).map(
    (index) => dataGrid.value.columnOption(index),
  );
  allColumns = allColumns.find((col) => col.sortIndex != null);
  return allColumns;
};

const setColumnChooserPosition = () => {
  var columnChooserView = dataGrid.value.getView("columnChooserView");
  if (!columnChooserView._popupContainer) {
    columnChooserView._initializePopupContainer();
    columnChooserView.render();
  }
  columnChooserView._popupContainer.option("position", {
    of: dataGrid.value.element(),
    my: "left top",
    at: "left top",
    offset: "0 50",
  });
};

const onEditorPreparing = (e) => {
  if (e.parentType == "filterRow") {
    e.editorOptions.placeholder = "Search";
  }
};

const clearFilter = () => {
  dataGrid.value.clearFilter("row");
  dataGrid.value.clearFilter("header");
  dataGrid.value.clearFilter("search");
  dataGrid.value.clearSorting();
};

const customLoad = async () => {
  await gridPreferencesStore
    .onTableHeader({
      url: "/jobs-grid",
      companyId: companyId.value,
    })
    .catch(() => {});
  let value = gridPreferencesStore.tableHeaders;
  return value;
};

const customSave = (gridState) => {
  gridState.pageIndex = 0;
  gridState.pageSize = 10;
  let value = JSON.stringify(gridState);
  value = value.replace(',"selectedRowKeys":[]', "");

  if (!gridPreferencesStore.tableHeaders) {
    gridPreferencesStore
      .createTableHeaders({
        url: "/jobs-grid",
        headers: value,
        filters: "[]",
        companyId: companyId.value,
      })
      .catch(() => {});
  } else if (value != JSON.stringify(gridPreferencesStore.tableHeaders)) {
    gridPreferencesStore
      .updateTableHeaders({
        url: "/jobs-grid",
        headers: value,
        filters: "[]",
        companyId: companyId.value,
        adminPreferenceId: adminPreferenceId.value,
      })
      .catch(() => {});
  }};

const onItemClick = (type) => {
  if (type == "Excel") {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Main sheet");
    exportDataGrid({
      component: dataGrid.value,
      worksheet: worksheet,
      customizeCell: function (options) {
        const excelCell = options;
        excelCell.font = { name: "Arial", size: 12 };
        excelCell.alignment = { horizontal: "left" };
      },
    }).then(function () {
      workbook.xlsx.writeBuffer().then(function (buffer) {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "jobs.xlsx",
        );
      });
    });
    // e.cancel = true;
  } else if (type == "CSV") {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Main sheet");
    exportDataGrid({
      component: dataGrid.value,
      worksheet: worksheet,
      customizeCell: function (options) {
        const excelCell = options;
        excelCell.font = { name: "Arial", size: 12 };
        excelCell.alignment = { horizontal: "left" };
      },
    }).then(function () {
      workbook.csv.writeBuffer().then(function (buffer) {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "jobs.csv",
        );
      });
    });
  } else if (type == "PDF") {
    const doc = new jsPDF("landscape");
    exportDataGridToPdf({
      jsPDFDocument: doc,
      component: dataGrid.value,
      customizeCell: function (options) {
        const { gridCell, pdfCell } = options;
        if (gridCell.rowType === "data") {
          // data columns style
          pdfCell.styles = {
            font: "times",
            fontStyle: "lato",
            fontSize: 8,
            cellWidth: "auto",
          };
        } else {
          // header columns style
          pdfCell.styles = {
            font: "times",
            fontStyle: "lato",
            fontSize: 8,
            fillColor: "#2680EB",
            textColor: "#FFFFFF",
            cellWidth: "auto",
          };
        }
      },
    }).then(() => {
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        const pageSize = doc.internal.pageSize;
        const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
        const pageHeight = pageSize.height
          ? pageSize.height
          : pageSize.getHeight();
        const header = "Agents List";
        const headerWidth = doc.getTextDimensions(header).w;
        const footer = `Page ${i} of ${pageCount}`;

        // Header
        doc.text(header, (pageWidth - headerWidth) / 2, 10);

        // Footer
        doc.text(
          footer,
          pageWidth / 2 - doc.getTextWidth(footer) / 2,
          pageHeight - 5,
          { baseline: "bottom" },
        );
      }

      doc.save("jobs.pdf");
    });
  }
};

const formattingString = (str) => {
  if (!str) return "";
  return str
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const getIntegrationJobsGrid = () => {
  dataGrid.value.beginCustomLoading();
  integrationsStore
    .fetchIntegrationJobs(companyId.value)
    .then((res) => {
      jobsGrid.value = res.data.map((row) => {
        row.label = row.label ? row.label : "-";
        row.type = row.type ? formattingString(row.type) : "-";
        row.error_message = row.error_message
          ? JSON.parse(row.error_message)
          : "-";
        row.status_text = row.status
          ? row.status.charAt(0).toUpperCase() +
            row.status.slice(1).toLowerCase()
          : "-";
        row.created_at = moment(row.created_at).format("DD/MM/YYYY - hh:mm A");
        return row;
      });
      dataGrid.value.endCustomLoading();
    })
    .catch(() => {
      dataGrid.value.endCustomLoading();
    });
};

onMounted(() => {
  jobsInterval = setInterval(() => {
    getIntegrationJobsGrid();
  }, JOB_POLL_INTERVAL_MS);
});

onBeforeUnmount(() => {
  if (jobsInterval) clearInterval(jobsInterval);
});

onBeforeMount(() => {
  setTimeout(() => {
    getIntegrationJobsGrid();
  }, 100);
});
</script>

<template>
  <AgentLayout>
    <!-- <div class="flex items-center">
        <h4 class="grid-header-title">Agents</h4>
        <button :title="checkLoggedInUserRole() ? 'You are not authorized to perform this action!': ''" :disabled="checkLoggedInUserRole()" @click="agentModal('show')" pill class="grid-header-action-btn">
          Add
        </button>
      </div> -->

    <PageHeader
      title="Jobs"
      subtitle="Connect and manage your integration jobs."
      :showButton="false"
    />

    <section class="grid-white-section main-page-container-animation card">
      <DxDataGrid height="100%"
        :data-source="jobsGrid"
        ref="dataGridRefName"
        :remote-operations="true"
        :showBorders="false"
        :columnAutoWidth="true"
        :allowColumnReordering="true"
        :allowColumnResizing="true"
        :rowAlternationEnabled="false"
        :showColumnLines="false"
        :showRowLines="true"
        :repaint-changes-only="true"
        :two-way-binding-enabled="true"
        :no-data-text="'No jobs yet'"
        @editor-preparing="onEditorPreparing($event)"
        @content-ready="contentReady($event)"
      >
        <DxLoadPanel :show-indicator="true" :show-pane="true" :enabled="true" />
        <DxScrolling mode="standard" :use-native="true" />
        <DxPaging :page-size="10" />
        <DxStateStoring
          :enabled="true"
          type="custom"
          :customSave="customSave"
          :customLoad="customLoad"
        />

        <DxPager
          :visible="true"
          :allowed-page-sizes="[10, 25, 50, 100]"
          :display-mode="'full'"
          :show-page-size-selector="true"
          :show-info="true"
          :show-navigation-buttons="true"
        />

        <!-- <DxFilterRow :visible="true" /> -->
        <DxSearchPanel :width="200" :visible="true" placeholder="Search ..." />
        <DxSorting mode="single" />
        <DxHeaderFilter :visible="true" />
        <DxColumnChooser :enabled="true" mode="select" />

        <DxToolbar>
          <DxItem location="before" template="columnButtonTemplate" />
          <DxItem location="before" template="exportButtonTemplate" />
          <DxItem location="before" template="clearAllTemplate" />
          <DxItem location="after" name="searchPanel" />
        </DxToolbar>

        <DxColumn
          data-field="label"
          caption="Name"
          :allow-header-filtering="true"
          :allow-filtering="true"
          :filter-operations="allowedOperations"
          data-type="string"
          :allow-searching="true"
        />

        <DxColumn
          data-field="type"
          caption="Type"
          :allow-header-filtering="true"
          :filter-operations="allowedOperations"
          data-type="string"
          :allow-searching="true"
        />

        <DxColumn
          data-field="error_message"
          caption="Error Message"
          :allow-header-filtering="false"
          :allow-filtering="true"
          :filter-operations="allowedOperations"
          data-type="string"
          :allow-searching="true"
        />

        <DxColumn
          data-field="status_text"
          caption="Status"
          :allow-header-filtering="true"
          :filter-operations="allowedOperations"
          data-type="string"
          :allow-searching="true"
          cell-template="statusCellTemplate"
        />

        <DxColumn
          data-field="created_at"
          format="yyyy/MM/dd HH:mm:ss"
          caption="Created At"
          :allow-editing="false"
          :allow-header-filtering="false"
          :allow-filtering="false"
          :allow-searching="false"
          data-type="string"
        />

        <template #statusCellTemplate="{ data }">
          <span class="status-badge" :class="`status-${data.data.status}`">{{
            data.value
          }}</span>
        </template>

        <!-- <template #columnButtonTemplate>
            <button class="custom-columns mx-1" @click="dataGrid.showColumnChooser()">
              <span class="custom-text">Columns </span>
              <span class="custom-icon">
                <i class="dx-icon dx-icon-spindown dx-icon-right"></i>
              </span>
            </button>
          </template>

          <template #exportButtonTemplate>
            <DxDropDownButton class="exportGridHeader" :items="buttonData" text="Export" @item-click="onItemClick" />
          </template>

          <template #clearAllTemplate>
            <div v-if="showClearAll" class="clear-cols">
              <div class="custom-line"></div>
              <Button
                text="Clear All"
                @click="clearFilter()"
                styling-mode="outlined"
                type="danger"
                class="clear-colums-filter"
              />
            </div>
          </template> -->

        <template #exportButtonTemplate>
          <button
            @click="onItemClick('Excel')"
            type="button"
            class="btn icon-button m-5 font-20"
          >
            <i class="bi bi-filetype-xlsx"></i>
          </button>
        </template>

        <template #columnButtonTemplate>
          <button
            @click="dataGrid.showColumnChooser()"
            type="button"
            class="btn icon-button font-20 m-5"
          >
            <i class="bi bi-columns"></i>
          </button>
        </template>

        <template #clearAllTemplate>
          <div v-if="showClearAll" class="clear-cols">
            <div class="custom-line"></div>
            <button
              @click="clearFilter()"
              type="button"
              class="btn action-btn clear-colums-filter m-5"
            >
              Clear Filters
            </button>
          </div>
        </template>
      </DxDataGrid>
    </section>
  </AgentLayout>
</template>
